import { writable } from 'svelte/store'
import { createLocalStorage, getLocalStorage, CART_STORAGE_NAME, DB_LINES_STORAGE_NAME, DB_LINES_RATE, deleteLocalStorage } from './storageUtils'
import { getClient } from './session'
import { fetchJson } from './fetchData'
import { loading } from './stores/loading'
import { translate } from './i18n'
import { alert } from './stores/alert'
import { getRate } from './rate'
import { getAccessories, getArticleParts } from './models'

const cartStore = writable('', set => {
    set(getLocalStorage(CART_STORAGE_NAME) ?? '')
})

const setShoppingCart = (cart) => {
    createLocalStorage({ name: CART_STORAGE_NAME, value: cart })
    cartStore.set(cart)
}

const formatShoppingCartData = items => {
    return items.map(item => {
        const materials = {};
        const accessories = {};
        item.materials.forEach(mat => {
            materials[mat.part] = mat.fabric;
        });
        item.accessories.forEach(acc => {
            accessories[acc.type] = acc.accessory;
        });
        const scItem = {
            drawableUUID: item.drawableUUID,
            identifier: item.article,
            name: item.name,
            shoppingCartData: {
                quantity: item.units,
                remarks: ""
            },
            sofa: [item.drawableUUID],
            materials,
            accessories,
            price: 0,
            size: { w: 0, h: 0, d: 0 },
            modelName: ""
        }
        return scItem;
    });
}

const getPrice = async cartData => {
    const clientId = getClient();
    if (!clientId) {
        return null;
    }

    const params = {
        cartData: JSON.stringify(cartData),
        clientId: Number(clientId)
    }

    try {
        return await fetchJson({
            endpoint: 'shoppingcart', 
            params, 
            customErrorString: "alerts_errors_noShoppingCartPrice"
        });
    }
    catch (err) {
        alert.newAlert({ message: translate('alerts_errors_noShoppingCartPrice'), type: 'error' });
    }
}

const getDBLines = () => {
    try {
        const lines = JSON.parse(getLocalStorage(DB_LINES_STORAGE_NAME));
        return lines;
    }
    catch (e) {
        return [];
    }
}

const saveDBLines = async (lines) => {
    try {
        createLocalStorage({ name: DB_LINES_STORAGE_NAME, value: JSON.stringify(lines) });
        const { data: rate } = await getRate();
        createLocalStorage({ name: DB_LINES_RATE, value: JSON.stringify(rate) });
    }
    catch (err) {
        console.error(err);
    }
}

const removeDBLines = () => {
    deleteLocalStorage(DB_LINES_STORAGE_NAME);
}

const dbLinesAvailable = () => {
    try {
        const lines = JSON.parse(getLocalStorage(DB_LINES_STORAGE_NAME));
        return lines.length > 0;
    }
    catch (e) {
        return false;
    }
}

const convertBudgetToShoppingCart = async (orderId) => {
    try {
        const response = await fetchJson({
            endpoint: 'opencart',
            params: { orderId },
            customErrorString: "alerts_errors_convertBudgetToShoppingCart"
        });

        const { dbItems } = response;
        const localStorageItems = [];
        for (const dbItem of dbItems) {
            const {
                price, drawableUUID, identifier, name, materials, accessories, realIdentifier, shoppingCartData
            } = dbItem;
            
            const partsData = await getArticleParts(identifier);
            const accessoriesData = await getAccessories(identifier);

            const item = {
                drawableUUID,
                article: identifier,
                name,
                units: shoppingCartData.quantity,
                price,
                realIdentifier,
                materials: Object.entries(materials).map(([partId, fabric]) => {
                    const matData = {};
                    const partData = partsData.find(({ part }) => part === partId);
                    if (partData) {
                        matData.part = partData.part;
                        matData.partName = partData.partName;
                        matData.fabric = fabric;
                        matData.description = fabric;

                        // Estos datos no se están cargando, pero en principio no son necesarios
                        matData.serie = "";
                        matData.type = "";
                    }
                    return matData
                }),
                accessories: Object.entries(accessories).map(([type, accessory]) => {
                    const accData = {};
                    const accTypeData = accessoriesData.find((accItem) => accItem.id === accessory && accItem.type === type);
                    if (accTypeData) {
                        accData.type = accTypeData.type;
                        accData.typeName = accTypeData.typeName;
                        accData.accessory = accTypeData.id;
                        accData.accessoryName = accTypeData.name;

                        // Estos datos no se cargan, pero no son necesarios
                        accData.units = 1;
                        accData.ŕequired = false;
                    }
                    return accData;
                }),
                finalPrice: price.finalPrice // TODO: pasar a string
            }
            localStorageItems.push(item);
        }

        await saveDBLines(localStorageItems);
        localStorage.setItem("currentShoppingCart", orderId);
        return localStorageItems;
    }
    catch (err) {
        alert.newAlert({ message: translate('alerts_errors_convertBudgetToShoppingCart'), type: 'error' });
    }
}

export { cartStore, setShoppingCart, getPrice, formatShoppingCartData, getDBLines, saveDBLines, removeDBLines, dbLinesAvailable, convertBudgetToShoppingCart }
